import React from 'react';
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  Chip
} from '@mui/material';
import {
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';

/**
 * CopyRoleMenus Component
 * 
 * Handles both individual role copying and bulk "copy all roles" functionality.
 * Extracted from StaffingStep for better maintainability.
 */
const CopyRoleMenus = ({
  // Individual role copy
  copyMenuAnchor,
  roleToCopy,
  onCopyRoleClose,
  onCopyRoleToMilestone,
  
  // Copy all roles
  copyAllRolesMenuAnchor,
  sourceMilestoneForCopy,
  onCopyAllRolesClose,
  onCopyAllRolesToMilestone,
  
  // Data
  formData
}) => {
  return (
    <>
      {/* Individual Role Copy Menu */}
      <Menu
        anchorEl={copyMenuAnchor}
        open={Boolean(copyMenuAnchor)}
        onClose={onCopyRoleClose}
      >
        {formData?.milestones?.map((milestone) => (
          <MenuItem
            key={milestone.id}
            onClick={() => onCopyRoleToMilestone(milestone)}
            disabled={milestone.id === roleToCopy?.milestone_id}
          >
            Copy to {milestone.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Copy All Roles Menu */}
      <Menu
        anchorEl={copyAllRolesMenuAnchor}
        open={Boolean(copyAllRolesMenuAnchor)}
        onClose={onCopyAllRolesClose}
        PaperProps={{
          sx: { minWidth: 280 }
        }}
      >
        <MenuItem disabled sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Copy all roles from "{sourceMilestoneForCopy?.name}" to:
        </MenuItem>
        
        {formData?.milestones
          ?.filter(milestone => milestone.id !== sourceMilestoneForCopy?.id)
          ?.map((milestone) => {
            const hasExistingRoles = milestone.roles && milestone.roles.length > 0;
            return (
              <MenuItem
                key={milestone.id}
                onClick={() => onCopyAllRolesToMilestone(milestone)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: hasExistingRoles ? 'warning.light' : 'action.hover'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <FileCopyIcon sx={{ fontSize: '0.9rem', color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={500}>
                    {milestone.name}
                  </Typography>
                  {hasExistingRoles && (
                    <Chip
                      label={`${milestone.roles.length} existing`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      sx={{ fontSize: '0.6rem', height: 16, ml: 'auto' }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                  {dayjs(milestone.start_date).format('MMM YYYY')} - {dayjs(milestone.end_date).format('MMM YYYY')}
                  {hasExistingRoles && ' • Will add to existing roles'}
                </Typography>
              </MenuItem>
            );
          })}
          
        {formData?.milestones?.filter(m => m.id !== sourceMilestoneForCopy?.id).length === 0 && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No other milestones available
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CopyRoleMenus;